import {
  expandRemovedProductServiceIds,
  getUpstreamAddItemActionNodes,
  getUpstreamRemovedProductServiceIds
} from "./wizardUpstreamRemovals";

export const DEFAULT_WIZARD_INSERT_POSITION = { mode: "end", anchor: null };

export const THIS_ITEM_PREVIEW_ID = "this";

const VALID_INSERT_MODES = new Set(["end", "start", "before", "after"]);

/**
 * Legacy add_item snapshots have no wizard_insert_position; treat those as "end of list".
 */
export function normalizeWizardInsertPosition(insertPosition) {
  if (!insertPosition || typeof insertPosition !== "object" || Array.isArray(insertPosition)) {
    return { ...DEFAULT_WIZARD_INSERT_POSITION };
  }
  const mode = insertPosition.mode;
  if (!VALID_INSERT_MODES.has(mode)) {
    return { ...DEFAULT_WIZARD_INSERT_POSITION };
  }
  if ((mode === "before" || mode === "after") && insertPosition.anchor?.id) {
    return {
      mode,
      anchor: {
        kind: insertPosition.anchor.kind === "add_item_action" ? "add_item_action" : "template",
        id: String(insertPosition.anchor.id)
      }
    };
  }
  if (mode === "start") {
    return { mode: "start", anchor: null };
  }
  return { ...DEFAULT_WIZARD_INSERT_POSITION };
}

export function withDefaultWizardInsertPosition(product) {
  if (!product || typeof product !== "object") return product;
  return {
    ...product,
    wizard_insert_position: normalizeWizardInsertPosition(product.wizard_insert_position)
  };
}

function getParentId(ps) {
  const parent = ps?.product_service_parent_id;
  if (Array.isArray(parent)) return parent[0] != null ? String(parent[0]) : null;
  if (parent) return String(parent);
  return null;
}

export function getWizardBundleParentId(ps) {
  if (!ps) return null;
  const addTo = ps.add_to_bundle;
  // Explicit "none" wins over leftover parent ids from a previous bundle edit.
  if (addTo === "none") return null;
  if (addTo != null && addTo !== "") return String(addTo);
  const parentId = getParentId(ps);
  if (parentId != null && parentId !== "" && parentId !== "none") return parentId;
  return null;
}

export function isWizardBundleChildProduct(ps) {
  return Boolean(getWizardBundleParentId(ps));
}

export function formatWizardProductPreviewLabel(ps) {
  if (!ps) return "This item";
  const description = String(ps.short_description || "").trim();
  const qty = ps.quantity != null && ps.quantity !== "" ? Number(ps.quantity) : NaN;
  if (description && Number.isFinite(qty)) {
    return `${description} - Qty: ${qty}`;
  }
  if (description) return description;
  return "This item";
}

function sortByPosition(items = []) {
  return [...items].sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
}

function resolveMappedId(id, referenceMap = {}) {
  if (id == null || id === "") return null;
  return String(referenceMap[id] ?? referenceMap[String(id)] ?? id);
}

function findProductById(productServices, id) {
  if (id == null || id === "") return null;
  const target = String(id);
  const list = productServices || [];
  for (let i = 0; i < list.length; i += 1) {
    const ps = list[i];
    if (String(ps?.document_item_product_service_id) === target) return ps;
    const nested = (ps?.children || []).find((child) => String(child?.document_item_product_service_id) === target);
    if (nested) return nested;
  }
  return null;
}

export function findWizardBundleParent({
  templateProductServices = [],
  nodes = [],
  edges = [],
  currentNodeId,
  parentId
} = {}) {
  if (parentId == null || parentId === "") return null;
  const fromTemplate = findProductById(templateProductServices, parentId);
  if (fromTemplate) return fromTemplate;
  const upstream = getUpstreamAddItemActionNodes(nodes, edges, currentNodeId);
  for (let i = 0; i < upstream.length; i += 1) {
    const ps = upstream[i]?.data?.nodeProps?.answers?.new_product_object;
    if (ps && String(ps.document_item_product_service_id) === String(parentId)) return ps;
  }
  return null;
}

function collectBundleChildren(parent, allProducts = []) {
  const parentId = parent?.document_item_product_service_id;
  if (parentId == null) return [];
  const nested = [...(parent.children || [])];
  const seen = new Set(nested.map((child) => String(child?.document_item_product_service_id)));
  (allProducts || []).forEach((ps) => {
    if (!ps?.is_inside_bundle) return;
    const id = ps.document_item_product_service_id;
    if (id == null || seen.has(String(id))) return;
    if (getWizardBundleParentId(ps) !== String(parentId)) return;
    nested.push(ps);
    seen.add(String(id));
  });
  return sortByPosition(nested.filter((child) => child?.document_item_product_service_id));
}

function cloneExistingItems(existingItems = []) {
  return (existingItems || []).map((ps) => ({
    ...ps,
    children: Array.isArray(ps?.children) ? [...ps.children] : []
  }));
}

function findIndexByProductServiceId(list, refId) {
  if (refId == null || refId === "") return -1;
  return list.findIndex((item) => String(item?.document_item_product_service_id) === String(refId));
}

function findPreviewAnchorIndex(rows, anchor) {
  if (!anchor?.id) return -1;
  return rows.findIndex((row) => row.kind === anchor.kind && String(row.id) === String(anchor.id));
}

/**
 * Resolves where to splice an item into a live working array.
 * Later items that share a start/after hint take the exact slot (same as the
 * builder preview), so Finalize order matches what the user arranged.
 */
export function getWorkingInsertIndex(list, hint) {
  const mode = hint?.mode || "end";
  const refId = hint?.refId;

  if (mode === "start") {
    return 0;
  }

  if (mode === "after" && refId != null && refId !== "") {
    const idx = findIndexByProductServiceId(list, refId);
    if (idx < 0) return list.length;
    return idx + 1;
  }

  if (mode === "before" && refId != null && refId !== "") {
    const idx = findIndexByProductServiceId(list, refId);
    if (idx < 0) return list.length;
    return idx;
  }

  return list.length;
}

function getPreviewInsertIndex(rows, insertPosition) {
  const normalized = normalizeWizardInsertPosition(insertPosition);
  const mode = normalized.mode;
  const anchor = normalized.anchor;

  if (mode === "start") {
    return 0;
  }

  if (mode === "after" && anchor?.id) {
    const idx = findPreviewAnchorIndex(rows, anchor);
    if (idx < 0) return rows.length;
    return idx + 1;
  }

  if (mode === "before" && anchor?.id) {
    const idx = findPreviewAnchorIndex(rows, anchor);
    if (idx < 0) return rows.length;
    return idx;
  }

  return rows.length;
}

export function insertPositionFromPreviewRows(rows = []) {
  const thisIndex = rows.findIndex((row) => row.isThisItem);
  const others = rows.filter((row) => !row.isThisItem);
  if (!others.length || thisIndex < 0) {
    return { ...DEFAULT_WIZARD_INSERT_POSITION };
  }
  if (thisIndex <= 0) {
    return { mode: "start", anchor: null };
  }
  if (thisIndex === rows.length - 1) {
    return { ...DEFAULT_WIZARD_INSERT_POSITION };
  }
  const prev = rows[thisIndex - 1];
  return {
    mode: "after",
    anchor: { kind: prev.kind, id: String(prev.id) }
  };
}

export function resolveRuntimeInsertPosition(wizardInsertPosition, maps = {}) {
  const mode = normalizeWizardInsertPosition(wizardInsertPosition).mode;
  if (mode === "start" || mode === "end") {
    return { mode, refId: null };
  }

  const kind = wizardInsertPosition?.anchor?.kind;
  const id = wizardInsertPosition?.anchor?.id;
  if (id == null || id === "") {
    return { mode: "end", refId: null };
  }

  let refId = null;
  if (kind === "template") {
    const referenceMap = maps.referenceProductServiceId || {};
    refId = referenceMap[id] ?? referenceMap[String(id)] ?? id;
  } else if (kind === "add_item_action") {
    const actionMap = maps.addItemActionProductServiceMap || {};
    refId = actionMap[id] ?? actionMap[String(id)] ?? null;
  }

  if (refId == null || refId === "") {
    return { mode: "end", refId: null };
  }
  return { mode, refId: String(refId) };
}

function getActionNodeBlockId(node, ps) {
  const actionId = node?.data?.nodeProps?.id;
  const answers = node?.data?.nodeProps?.answers || {};
  return answers[`document_item_id_${actionId}`] ?? ps?.document_item_id ?? null;
}

function mapPreviewRow(ps, kind, id) {
  return {
    id: String(id),
    kind,
    label: formatWizardProductPreviewLabel(ps),
    isThisItem: false
  };
}

function appendThisItemRow(rows, thisItemLabel, thisItemInsertPosition) {
  const thisRow = {
    id: THIS_ITEM_PREVIEW_ID,
    kind: "this",
    label: thisItemLabel || "This item",
    isThisItem: true
  };
  const thisIndex = getPreviewInsertIndex(rows, normalizeWizardInsertPosition(thisItemInsertPosition));
  rows.splice(thisIndex, 0, thisRow);
  return rows;
}

export function buildWizardAddItemPreviewRows({
  templateProductServices = [],
  nodes = [],
  edges = [],
  currentNodeId,
  documentItemId,
  thisItemLabel,
  thisItemInsertPosition,
  parentProductServiceId
} = {}) {
  const blockId = String(documentItemId ?? "");
  const removedIds = expandRemovedProductServiceIds(
    templateProductServices,
    getUpstreamRemovedProductServiceIds(nodes, edges, currentNodeId)
  );

  if (parentProductServiceId) {
    const parent = findWizardBundleParent({
      templateProductServices,
      nodes,
      edges,
      currentNodeId,
      parentId: parentProductServiceId
    });
    const rows = collectBundleChildren(parent, templateProductServices)
      .filter((ps) => !removedIds.has(String(ps.document_item_product_service_id)))
      .map((ps) => mapPreviewRow(ps, "template", ps.document_item_product_service_id));

    getUpstreamAddItemActionNodes(nodes, edges, currentNodeId).forEach((node) => {
      const actionId = node.data?.nodeProps?.id;
      const ps = node.data?.nodeProps?.answers?.new_product_object;
      if (!actionId || !ps || !isWizardBundleChildProduct(ps)) return;
      if (getWizardBundleParentId(ps) !== String(parentProductServiceId)) return;
      if (ps.document_item_product_service_id && removedIds.has(String(ps.document_item_product_service_id))) {
        return;
      }
      const insertIndex = getPreviewInsertIndex(rows, normalizeWizardInsertPosition(ps.wizard_insert_position));
      rows.splice(insertIndex, 0, mapPreviewRow(ps, "add_item_action", actionId));
    });

    return appendThisItemRow(rows, thisItemLabel, thisItemInsertPosition);
  }

  const rows = (templateProductServices || [])
    .filter((ps) => !ps?.is_inside_bundle && String(ps?.document_item_id ?? "") === blockId)
    .filter((ps) => ps?.document_item_product_service_id && !removedIds.has(String(ps.document_item_product_service_id)))
    .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
    .map((ps) => mapPreviewRow(ps, "template", ps.document_item_product_service_id));

  getUpstreamAddItemActionNodes(nodes, edges, currentNodeId).forEach((node) => {
    const actionId = node.data?.nodeProps?.id;
    const ps = node.data?.nodeProps?.answers?.new_product_object;
    if (!actionId || !ps || isWizardBundleChildProduct(ps)) return;

    const nodeBlockId = getActionNodeBlockId(node, ps);
    if (nodeBlockId != null && String(nodeBlockId) !== blockId) return;
    if (ps.document_item_product_service_id && removedIds.has(String(ps.document_item_product_service_id))) {
      return;
    }

    const insertIndex = getPreviewInsertIndex(rows, normalizeWizardInsertPosition(ps.wizard_insert_position));
    rows.splice(insertIndex, 0, mapPreviewRow(ps, "add_item_action", actionId));
  });

  return appendThisItemRow(rows, thisItemLabel, thisItemInsertPosition);
}

/**
 * Assigns 1-based `position` to wizard add_item rows (and shifts existing rows
 * in the same block or bundle) according to each item's `_insertPosition` hint.
 */
export function resolveWizardAddItemPositions(existingItems = [], itemsToAdd = [], referenceProductServiceId = {}) {
  const updatedExistingItems = cloneExistingItems(existingItems);
  const blocks = new Map();
  const touchedBlocks = new Set();
  const childrenByParent = new Map();
  const touchedParents = new Set();

  updatedExistingItems.forEach((ps) => {
    if (ps?.is_inside_bundle) return;
    const blockKey = String(ps.document_item_id ?? "");
    if (!blocks.has(blockKey)) blocks.set(blockKey, []);
    blocks.get(blockKey).push(ps);
  });
  blocks.forEach((list) => {
    list.sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
  });

  const itemsWithPositions = [];

  const findParent = (parentId) => {
    if (!parentId) return null;
    const resolvedId = resolveMappedId(parentId, referenceProductServiceId);
    const match = (ps) => {
      const id = ps?.document_item_product_service_id;
      if (id == null) return false;
      return String(id) === String(parentId) || String(id) === String(resolvedId);
    };
    return (
      updatedExistingItems.find(match) ||
      itemsWithPositions.find(match) ||
      [...blocks.values()].flat().find(match) ||
      null
    );
  };

  const getWorkingChildren = (rawParentId) => {
    const resolvedId = resolveMappedId(rawParentId, referenceProductServiceId) || String(rawParentId);
    const key = resolvedId;
    if (childrenByParent.has(key)) return { key, list: childrenByParent.get(key) };
    const parent = findParent(rawParentId);
    const list = parent ? collectBundleChildren(parent, updatedExistingItems) : [];
    childrenByParent.set(key, list);
    return { key, list };
  };

  (itemsToAdd || []).forEach((raw) => {
    const { _insertPosition, wizard_insert_position: _wizardInsertPosition, ...item } = raw || {};
    const rawParentId = getWizardBundleParentId(item);
    if (rawParentId) {
      const { key, list } = getWorkingChildren(rawParentId);
      const insertIndex = getWorkingInsertIndex(list, _insertPosition);
      list.splice(insertIndex, 0, item);
      touchedParents.add(key);
      itemsWithPositions.push(item);
      return;
    }

    const blockKey = String(item.document_item_id ?? "");
    if (!blocks.has(blockKey)) blocks.set(blockKey, []);
    const list = blocks.get(blockKey);
    const insertIndex = getWorkingInsertIndex(list, _insertPosition);
    list.splice(insertIndex, 0, item);
    touchedBlocks.add(blockKey);
    itemsWithPositions.push(item);
  });

  touchedBlocks.forEach((blockKey) => {
    (blocks.get(blockKey) || []).forEach((item, index) => {
      item.position = index + 1;
    });
  });

  touchedParents.forEach((parentKey) => {
    (childrenByParent.get(parentKey) || []).forEach((item, index) => {
      item.position = index + 1;
    });
  });

  return { itemsWithPositions, updatedExistingItems };
}
